import { useState } from 'react';
import { suggestDesign } from '../api';

export default function ResearchDesignPanel({ papers }) {
  const [subject, setSubject] = useState('');
  const [experimentalDesign, setExperimentalDesign] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = subject.trim() && experimentalDesign.trim() && papers.length > 0 && !loading;

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const response = await suggestDesign(subject.trim(), experimentalDesign.trim(), papers);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get design suggestions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Research Design Assistant</h2>
        <p className="text-sm text-[#4d4d4d] mt-1">
          Describe your research intent and we'll analyse your workspace papers to suggest a tailored protocol design.
        </p>
      </div>

      <div className="bg-white rounded-lg p-5 space-y-4" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
        <div>
          <label className="block text-sm font-medium text-[#171717] mb-1" style={{ letterSpacing: '-0.32px' }}>
            Research Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. CRISPR-Cas9 gene editing in HEK293T cells"
            className="w-full rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#171717] mb-1" style={{ letterSpacing: '-0.32px' }}>
            Experimental Design Goals
          </label>
          <textarea
            value={experimentalDesign}
            onChange={(e) => setExperimentalDesign(e.target.value)}
            placeholder="e.g. I want to knock out the TP53 gene and measure apoptosis rates over 7 days using flow cytometry. I'm aiming for a controlled in vitro study with 3 replicates."
            rows={4}
            className="w-full rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef] resize-none"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            disabled={loading}
          />
        </div>

        {papers.length === 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2" style={{ boxShadow: 'rgba(251,191,36,0.3) 0px 0px 0px 1px' }}>
            Add at least 1 paper to your workspace before generating suggestions.
          </p>
        )}

        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full px-4 py-2 bg-[#171717] text-white rounded-md font-medium text-sm
                     hover:bg-[#2e2e2e] disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Analysing papers...
            </>
          ) : (
            <>Get Design Suggestions ({papers.length} paper{papers.length !== 1 ? 's' : ''})</>
          )}
        </button>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      {result && (
        <div className="space-y-4">
          {result.mock_mode && (
            <div className="bg-amber-50 rounded-md px-4 py-2 text-xs text-amber-700 font-medium" style={{ boxShadow: 'rgba(251,191,36,0.3) 0px 0px 0px 1px' }}>
              MOCK MODE — Add your ANTHROPIC_API_KEY to backend/.env for real AI suggestions
            </div>
          )}

          <h3 className="text-sm font-medium text-[#4d4d4d]">
            Design Suggestions for: <span className="text-[#171717] font-semibold">{result.subject}</span>
          </h3>

          {result.suggestions.map((suggestion, idx) => (
            <div
              key={idx}
              className="bg-white rounded-lg p-4"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px' }}
            >
              <h4 className="font-semibold text-[#171717] text-sm mb-2" style={{ letterSpacing: '-0.32px' }}>{suggestion.title}</h4>
              <p className="text-sm text-[#4d4d4d] whitespace-pre-line leading-relaxed">
                {suggestion.content}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
